/**
 * Cliente HTTP para a API do site.
 *
 * - Dev: VITE_API_URL não definido → usa `/api` (proxy do Vite para o dev-server).
 * - Prod: build embute VITE_API_URL com o invoke_url do API Gateway, e o fetch
 *   vai direto para `${VITE_API_URL}/blog/posts` (o handler aceita com ou sem
 *   o prefixo `/api`).
 *
 * Convenção: respostas têm `{ data, status, timestamp }` no sucesso e
 * `{ error: { code, message }, status }` no erro.
 */

const API_BASE = (import.meta.env.VITE_API_URL ?? '/api').replace(/\/+$/, '');

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Provedor de token de autenticação. O `AuthContext` registra um getter que
 * devolve o idToken do Cognito; quando presente, é enviado como Bearer nas
 * chamadas (rotas admin exigem). Rotas públicas funcionam sem token.
 */
let authTokenGetter: () => string | undefined = () => undefined;
export function setAuthTokenGetter(getter: () => string | undefined): void {
  authTokenGetter = getter;
}

/** Callback disparado num 401 (sessão expirada/ausente) — ex.: redirecionar ao login. */
let onUnauthorized: (() => void) | undefined;
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

interface SuccessEnvelope<T> {
  data: T;
  status: number;
}
interface ErrorEnvelope {
  error: { code: string; message: string; errors?: unknown };
  status: number;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = authTokenGetter();
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });

  if (res.status === 401) {
    onUnauthorized?.();
    throw new ApiError(401, 'Sessão expirada. Faça login novamente.');
  }

  if (res.status === 204) {
    return undefined as unknown as T;
  }

  const text = await res.text();
  const json = text ? JSON.parse(text) : {};

  if (!res.ok) {
    const envelope = json as ErrorEnvelope;
    throw new ApiError(res.status, envelope.error?.message ?? `Erro ${res.status}`);
  }

  const envelope = json as SuccessEnvelope<T>;
  return envelope.data;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) => request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
